import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPosts } from '../lib/blogStorage';

export default function Blog() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let active = true;

    getPosts({ published: true })
      .then((data) => {
        if (!active) return;
        setPosts(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Failed to load blog posts', err);
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <div>
      <Helmet>
        <title>Blog – Synex Agency</title>
        <meta
          name="description"
          content="Read the Synex Agency blog for practical tips on web design, AI automation, and growth strategies."
        />
      </Helmet>

      <section className="relative pt-32 pb-32 bg-brand rounded-b-[4rem] sm:rounded-b-[8rem] lg:rounded-b-[12rem] shadow-xl shadow-brand/20 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-brand via-brand/80 to-brand/60 rounded-b-[4rem] sm:rounded-b-[8rem] lg:rounded-b-[12rem] opacity-95"></div>
        <div className="relative max-w-6xl mx-auto px-6 text-center z-10">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white">
            Insights, Ideas, and <span className="text-white/80">AI-powered</span> Growth
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg text-white/80">
            A modern blog for founders and teams looking to build better products, automate processes, and scale with confidence.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="rounded-full bg-white px-8 py-3 text-sm font-semibold text-[#1c1f1e] shadow-md shadow-black/10 transition hover:bg-white/90"
            >
              Book a call
            </Link>
            <Link
              to="/projects"
              className="rounded-full border border-white/30 bg-white/10 px-8 py-3 text-sm font-semibold text-white transition hover:bg-white/20"
            >
              View our work
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-zinc-900">
                Latest Articles
              </h2>
              <p className="mt-2 text-sm text-zinc-500 max-w-2xl">
                Browse insights on design, automation, and scaling your business with modern technology.
              </p>
            </div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-6 py-3 text-sm font-semibold text-zinc-700 shadow-sm hover:border-brand hover:bg-white hover:text-zinc-900 transition"
            >
              Manage Posts
              <span aria-hidden="true">→</span>
            </Link>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group relative flex flex-col overflow-hidden rounded-[2.5rem] border border-zinc-100 bg-white shadow-sm ring-1 ring-zinc-200/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand/10 p-2"
              >
                <div className="relative h-48 w-full overflow-hidden rounded-[2rem] bg-gradient-to-br from-brand/20 via-brand/10 to-zinc-50">
                   <div className="absolute inset-0 bg-brand/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                </div>
                <div className="px-6 py-8 flex flex-col flex-1">
                  <h3 className="text-xl md:text-2xl font-bold text-zinc-900 mb-3 group-hover:text-brand transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm md:text-base text-zinc-500 mb-8 line-clamp-3 flex-1 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="mt-auto flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 transition-colors group-hover:bg-brand group-hover:text-white">
                      →
                    </span>
                    <span className="text-sm font-semibold tracking-wide text-zinc-900 group-hover:text-brand transition-colors">
                      Read article
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
